import type {
  ActorDatabaseHandle,
  ActorTransaction,
} from "@-/dev.improve.haniverse.database.actor";
import { actorSchema } from "@-/dev.improve.haniverse.database.schemas.actor.schema";
import { and, isNotNull, isNull, sql } from "@-/com.npmjs.drizzle-orm";
import { HAN_MILES_PER_KM } from "#han-miles";

/** Mode of a trip that rode a dragon: instant, paid with one dragon instead of Han. */
export const DRAGON_TRAVEL_MODE = "dragon";

export interface HanWallet {
  /** Dragons (龙) held: each carries the learner anywhere unlocked, instantly. */
  dragons: number;
  /**
   * Han (汉) held. It can go negative, e.g. when a review whose Han was already spent is undone;
   * nothing can be bought until it is positive again.
   */
  han: number;
  /**
   * HanMiles (✈) held: every journey not turned back from earns its distance in km, the daily 红包
   * adds more, and they take Han off flight fares (see `redeemHanMilesOn`).
   */
  hanMiles: number;
}

type ActorReader = ActorDatabaseHandle | ActorTransaction;

/**
 * The wallet, derived from the ledgers rather than stored: Han earned by live reviews plus live
 * rewards, minus live trips' fares and unlocked quests' costs; dragons from rewards minus dragon rides; HanMiles from the
 * distance of trips not turned back from plus rewards, minus those redeemed on fares. Undoing a
 * review or restoring a backup therefore never leaves a stale balance, and devices never conflict
 * over one.
 *
 * `select` does not filter soft-deleted rows the way `query.*` does, so every read filters them.
 */
export async function readHanWallet(db: ActorReader): Promise<HanWallet> {
  const [[reviews], [rewards], [trips], [quests]] = await Promise.all([
    db
      .select({ han: sql<number>`coalesce(sum(${actorSchema.userCardReview.hanEarned}), 0)` })
      .from(actorSchema.userCardReview)
      .where(
        and(
          isNull(actorSchema.userCardReview.deletedAt),
          isNotNull(actorSchema.userCardReview.hanEarned),
        ),
      ),
    db
      .select({
        dragons: sql<number>`coalesce(sum(${actorSchema.rewardClaim.dragonAmount}), 0)`,
        han: sql<number>`coalesce(sum(${actorSchema.rewardClaim.hanAmount}), 0)`,
        hanMiles: sql<number>`coalesce(sum(${actorSchema.rewardClaim.hanMilesAmount}), 0)`,
      })
      .from(actorSchema.rewardClaim)
      .where(isNull(actorSchema.rewardClaim.deletedAt)),
    db
      .select({
        dragons: sql<number>`coalesce(sum(case when ${actorSchema.travelTrip.mode} = ${DRAGON_TRAVEL_MODE} then 1 else 0 end), 0)`,
        han: sql<number>`coalesce(sum(${actorSchema.travelTrip.costHan}), 0)`,
        hanMilesEarnedKm: sql<number>`coalesce(sum(case when ${actorSchema.travelTrip.cancelledAt} is null then ${actorSchema.travelTrip.distanceKm} else 0 end), 0)`,
        hanMilesRedeemed: sql<number>`coalesce(sum(${actorSchema.travelTrip.hanMilesRedeemed}), 0)`,
      })
      .from(actorSchema.travelTrip)
      .where(isNull(actorSchema.travelTrip.deletedAt)),
    db
      .select({ han: sql<number>`coalesce(sum(${actorSchema.userQuest.costHan}), 0)` })
      .from(actorSchema.userQuest)
      .where(isNull(actorSchema.userQuest.deletedAt)),
  ]);
  return {
    dragons: Number(rewards?.dragons ?? 0) - Number(trips?.dragons ?? 0),
    han:
      Number(reviews?.han ?? 0) +
      Number(rewards?.han ?? 0) -
      Number(trips?.han ?? 0) -
      Number(quests?.han ?? 0),
    hanMiles:
      Number(trips?.hanMilesEarnedKm ?? 0) * HAN_MILES_PER_KM +
      Number(rewards?.hanMiles ?? 0) -
      Number(trips?.hanMilesRedeemed ?? 0),
  };
}

/** Whether a reward claim with this id exists, including an undone (soft-deleted) one. */
export async function hasRewardClaim(tx: ActorTransaction, id: string): Promise<boolean> {
  const row = await tx.queryRaw.rewardClaim.findFirst({ columns: { id: true }, where: { id } });
  return row !== undefined;
}
